import { useState, useEffect, useMemo } from 'react';
import { getProfitLoss } from '../services/api';
import { TrendDown, Wallet, Receipt, ArrowsClockwise } from '@phosphor-icons/react';

const MONTHS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

function eur(n) {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}

export default function GewinnVerlust() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getProfitLoss(year);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [year]);

  const maxBar = useMemo(() => {
    if (!data?.monthly_series) return 1;
    let max = 1;
    for (const m of data.monthly_series) {
      max = Math.max(max, m.income || 0, m.expenses || 0);
    }
    return max;
  }, [data]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-700" />
      </div>
    );
  }

  const profit = data.profit;
  const profitTone = profit >= 0 ? 'text-emerald-700' : 'text-red-700';
  const profitBg = profit >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200';

  return (
    <div className="space-y-6" data-testid="gewinn-verlust-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
            Gewinn &amp; Verlust
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Einnahmen (Mieten) − Ausgaben (Belege) für {year}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={load}
            className="border border-slate-200 px-3 py-2 rounded-lg text-sm inline-flex items-center gap-1.5 hover:bg-slate-50 transition-colors"
          >
            <ArrowsClockwise weight="bold" className="w-4 h-4" />
            Aktualisieren
          </button>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="border border-slate-200 px-3 py-2 rounded-lg text-sm bg-white"
            data-testid="gv-year-select"
          >
            {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile icon={Wallet} label="Mieteinnahmen" value={eur(data.income_total)} tone="bg-emerald-50 border-emerald-200 text-emerald-700" testId="kpi-income" />
        <KpiTile icon={TrendDown} label="Ausgaben (Belege)" value={eur(data.expense_total)} tone="bg-red-50 border-red-200 text-red-700" testId="kpi-expenses" />
        <div className={`border p-5 rounded-lg ${profitBg}`} data-testid="kpi-profit">
          <p className="text-xs uppercase tracking-widest font-bold opacity-75">Gewinn / Verlust</p>
          <p className={`font-mono text-3xl font-black mt-2 ${profitTone}`}>{eur(profit)}</p>
          <p className={`text-xs mt-1 ${profitTone} opacity-80`}>
            {data.income_total > 0
              ? `Marge: ${((profit / data.income_total) * 100).toFixed(1).replace('.', ',')}%`
              : 'Marge: —'}
          </p>
        </div>
        <KpiTile icon={Receipt} label="Offene Forderungen" value={eur(data.income_outstanding)} tone="bg-amber-50 border-amber-200 text-amber-700" testId="kpi-outstanding" />
      </div>

      {/* Status badges */}
      <div className="flex gap-3 text-xs">
        <span className="px-3 py-1 bg-emerald-100 text-emerald-800 border border-emerald-200 rounded">
          ✓ {data.stats.paid_count} bezahlt
        </span>
        <span className="px-3 py-1 bg-slate-100 text-slate-700 border border-slate-200 rounded">
          {data.stats.open_count} offen
        </span>
        <span className="px-3 py-1 bg-red-100 text-red-800 border border-red-200 rounded">
          ⚠ {data.stats.overdue_count} überfällig
        </span>
      </div>

      {/* Monthly chart */}
      <div className="bg-white border border-slate-200 rounded-lg" data-testid="monthly-chart">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="font-heading text-base sm:text-lg font-bold text-slate-900">Monatsverlauf {year}</h2>
        </div>
        <div className="p-5 overflow-x-auto">
          <div className="min-w-[720px]">
          <div className="flex items-stretch gap-2">
            {data.monthly_series.map(m => (
              <div key={m.month} className="flex-1 flex flex-col items-center">
                <div className="w-full h-48 flex items-end gap-0.5">
                  <div
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 transition-colors rounded-t min-h-[2px]"
                    style={{ height: `${(m.income / maxBar) * 100}%` }}
                    title={`${MONTHS[m.month-1]}: Einnahmen ${eur(m.income)}`}
                  />
                  <div
                    className="flex-1 bg-red-400 hover:bg-red-500 transition-colors rounded-t min-h-[2px]"
                    style={{ height: `${(m.expenses / maxBar) * 100}%` }}
                    title={`${MONTHS[m.month-1]}: Ausgaben ${eur(m.expenses)}`}
                  />
                </div>
                <p className="text-[10px] text-slate-500 mt-1">{MONTHS[m.month-1]}</p>
                <p className={`text-[10px] font-mono ${m.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {m.profit >= 0 ? '+' : ''}{Math.round(m.profit)}
                </p>
              </div>
            ))}
          </div>
          <div className="flex gap-4 mt-4 text-xs text-slate-600">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-500 rounded" /> Einnahmen</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-red-400 rounded" /> Ausgaben</span>
          </div>
          </div>
        </div>
      </div>

      {/* Monthly table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="font-heading text-base sm:text-lg font-bold text-slate-900">Monats-Tabelle</h2>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm" data-testid="monthly-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-slate-700">Monat</th>
              <th className="text-right px-3 py-2 font-medium text-emerald-700">Einnahmen</th>
              <th className="text-right px-3 py-2 font-medium text-red-700">Ausgaben</th>
              <th className="text-right px-3 py-2 font-medium text-slate-900">Saldo</th>
            </tr>
          </thead>
          <tbody>
            {data.monthly_series.map(m => (
              <tr key={m.month} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 text-slate-900">{MONTHS[m.month-1]} {year}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-700">{eur(m.income)}</td>
                <td className="px-3 py-2 text-right font-mono text-red-700">{eur(m.expenses)}</td>
                <td className={`px-3 py-2 text-right font-mono font-bold ${m.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {eur(m.profit)}
                </td>
              </tr>
            ))}
            <tr className="bg-slate-900 text-white font-bold">
              <td className="px-3 py-2">Gesamt {year}</td>
              <td className="px-3 py-2 text-right font-mono">{eur(data.income_total)}</td>
              <td className="px-3 py-2 text-right font-mono">{eur(data.expense_total)}</td>
              <td className="px-3 py-2 text-right font-mono">{eur(data.profit)}</td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>

      {/* Expenses by category */}
      {data.expenses_by_category && data.expenses_by_category.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <h2 className="font-heading text-base sm:text-lg font-bold text-slate-900">Ausgaben nach Kostenstelle</h2>
          </div>
          <div className="p-5 space-y-2">
            {data.expenses_by_category.map((c, i) => {
              const pct = data.expense_total > 0 ? (c.amount / data.expense_total) * 100 : 0;
              return (
                <div key={i}>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-700">{c.kostenstelle_name}</span>
                    <span className="font-mono text-slate-900">{eur(c.amount)} <span className="text-slate-400 text-xs">({pct.toFixed(0)}%)</span></span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded overflow-hidden mt-1">
                    <div className="h-full bg-red-400" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, tone, testId }) {
  return (
    <div className={`border p-5 rounded ${tone}`} data-testid={testId}>
      <p className="text-xs uppercase tracking-widest font-bold opacity-75 flex items-center gap-1.5">
        <Icon weight="bold" className="w-4 h-4" /> {label}
      </p>
      <p className="font-mono text-3xl font-black mt-2">{value}</p>
    </div>
  );
}
